import {
  FC,
  Fragment,
  MouseEvent,
  useCallback,
  useMemo,
  useState,
} from "react";
import { clsx } from "clsx";
import { InfoIcon, PlusIcon, WarningIcon } from "../../../assets/icons";
import { useGetApplicationVendors } from "../../../hooks";
import {
  LoanProductType,
  LoanVendorStatus,
  LoanVendorType,
  Vendor,
} from "../../../hooks/api/types";
import { formatAmount, formatISOString } from "../../../utils";
import {
  getVendorNature,
  getVendorTitle,
} from "../../LoanApplicationSteps/VendorInformation/utils";
import { LoanApplication } from "../../../contexts";

type ActiveVendorsSubLimitSectionProps = {
  loan: LoanApplication;
  onRequestDrawdown?: (vendor: {
    vendorId: string;
    name: string;
    subLimitAmount?: number | null;
    headroom?: number | null;
    outstandingAmount?: number | null;
  }) => void;
  drawdownDisabled?: boolean;
  defaultExpanded?: boolean;
  blockWhenSubLimitReached?: boolean;
  className?: string;
  /** Shown inline after the “Buyers/Suppliers limit” badge (e.g. drawdown hint). */
  headerHint?: string | null;
};

const DASH = "—";

/** Min content width so six columns stay readable; parent scrolls horizontally. */
const TABLE_MIN_WIDTH = "cx-min-w-[720px]";

/** Matches Figma table row: 6 columns with even distribution. */
const TABLE_GRID =
  "cx-grid cx-w-full cx-grid-cols-[minmax(0,1.1fr)_minmax(0,1.05fr)_minmax(0,0.95fr)_minmax(0,0.95fr)_minmax(0,1.05fr)_minmax(0,1.2fr)] cx-items-center cx-gap-x-2";

const PANEL_BORDER =
  "cx-rounded-lg cx-border-[0.5px] cx-border-solid cx-border-[#DDD] cx-bg-white";

const AmountText = ({
  amount,
  className,
}: {
  amount: number | null | undefined;
  className?: string;
}) => {
  if (amount == null) return <>{DASH}</>;
  return (
    <span
      className={clsx(
        "cx-text-sm cx-font-normal",
        className ?? "cx-text-black"
      )}
    >
      <span className="cx-font-aed-font">D </span>
      {formatAmount(amount)}
    </span>
  );
};

const formatContractExpiry = (date: string | null | undefined) => {
  if (!date) return DASH;
  const formatted = formatISOString(date, "d LLLL, yyyy");
  return formatted || DASH;
};

const resolveAvailable = (vendor: Vendor): number | null => {
  if (vendor.headroom != null) return vendor.headroom;
  const utilisation = vendor.outstandingAmount ?? null;
  if (utilisation == null || vendor.subLimitAmount == null) return null;
  return vendor.subLimitAmount - utilisation;
};

const ExpandChevron = ({ expanded }: { expanded: boolean }) => (
  <svg
    width="10"
    height="6"
    viewBox="0 0 12 7"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path
      d={expanded ? "M1 6L6 1L11 6" : "M1 1L6 6L11 1"}
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TABLE_HEADERS = [
  "name",
  "Assigned sub-limit",
  "Utilisation",
  "Available",
  "Contract expiry",
  "Drawdown",
] as const;

export const ActiveVendorsSubLimitSection: FC<
  ActiveVendorsSubLimitSectionProps
> = ({
  loan,
  onRequestDrawdown,
  drawdownDisabled = false,
  defaultExpanded = true,
  blockWhenSubLimitReached = true,
  className = "cx-px-6 cx-pb-5",
  headerHint = null,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const loanType = (loan.loanType || loan.loanProduct?.type) as LoanProductType;
  const isPayablesFacility = loanType === LoanProductType.LOC_PAYABLE_FINANCING;
  const vendorTitle = getVendorTitle(loanType);
  const vendorNature = getVendorNature(loanType);
  const sectionLabel = `Active ${vendorNature}s`;
  const limitBadgeLabel = `${vendorTitle}s limit`;

  const { data, isLoading, isError } = useGetApplicationVendors(
    {
      loanApplicationId: loan.id,
      disablePagination: true,
      status: LoanVendorStatus.ACTIVE,
      type: isPayablesFacility ? LoanVendorType.SUPPLIER : LoanVendorType.BUYER,
    },
    { enabled: expanded }
  );

  const vendors: Vendor[] = useMemo(() => data?.data ?? [], [data?.data]);

  const handleToggle = useCallback((event: MouseEvent) => {
    event.stopPropagation();
    setExpanded((prev) => !prev);
  }, []);

  const handleRequestDrawdown = useCallback(
    (event: MouseEvent, vendor: Vendor) => {
      event.stopPropagation();
      if (drawdownDisabled || !onRequestDrawdown || !vendor.vendorId) return;
      onRequestDrawdown({
        vendorId: vendor.vendorId,
        name: vendor.name,
        subLimitAmount: vendor.subLimitAmount ?? null,
        headroom: resolveAvailable(vendor),
        outstandingAmount: vendor.outstandingAmount ?? null,
      });
    },
    [drawdownDisabled, onRequestDrawdown]
  );

  const headerLabels = useMemo(
    () =>
      TABLE_HEADERS.map((header) =>
        header === "name" ? `${vendorTitle} name` : header
      ),
    [vendorTitle]
  );

  return (
    <div
      className={className}
      role="presentation"
      onClick={(event) => event.stopPropagation()}
    >
      <div className={clsx(PANEL_BORDER, "cx-overflow-hidden")}>
        <button
          type="button"
          onClick={handleToggle}
          className="cx-w-full cx-flex cx-flex-wrap cx-items-center cx-gap-x-3 cx-gap-y-2 cx-px-6 cx-py-4 cx-text-left cx-bg-white hover:cx-bg-[#FAFAFA]"
          aria-expanded={expanded}
        >
          <span className="cx-inline-flex cx-items-center cx-justify-center cx-w-6 cx-h-6 cx-rounded-full cx-border cx-border-[#D0D5DD] cx-text-interaction-button-primary-default cx-shrink-0">
            <ExpandChevron expanded={expanded} />
          </span>
          <span className="cx-text-sm cx-font-semibold cx-text-[#181D27]">
            {sectionLabel}
          </span>
          <span className="cx-inline-flex cx-items-center cx-gap-1.5 cx-text-xs cx-font-medium cx-text-[#535862]">
            <span
              className="cx-inline-block cx-w-1.5 cx-h-1.5 cx-rounded-full"
              style={{ backgroundColor: "#F1C21B" }}
            />
            {limitBadgeLabel}
          </span>
          {headerHint ? (
            <span className="cx-inline-flex cx-items-center cx-gap-1.5 cx-text-sm cx-font-normal cx-text-[#666] cx-min-w-0">
              <InfoIcon width={16} height={16} color="#0064FF" aria-hidden />
              <span className="cx-min-w-0">{headerHint}</span>
            </span>
          ) : null}
        </button>
      </div>

      {expanded && (
        <div className="cx-mt-6">
          {isLoading && (
            <div
              className={clsx(
                PANEL_BORDER,
                "cx-px-6 cx-py-8 cx-text-sm cx-text-[#535862]"
              )}
            >
              Loading {vendorNature}s…
            </div>
          )}
          {isError && (
            <div
              className={clsx(
                PANEL_BORDER,
                "cx-px-6 cx-py-8 cx-text-sm cx-text-[#A2191F]"
              )}
            >
              Couldn’t load {vendorNature}s. Please try again.
            </div>
          )}
          {!isLoading && !isError && vendors.length === 0 && (
            <div
              className={clsx(
                PANEL_BORDER,
                "cx-px-6 cx-py-8 cx-text-sm cx-text-[#535862]"
              )}
            >
              No active {vendorNature}s yet.
            </div>
          )}
          {!isLoading && !isError && vendors.length > 0 && (
            <div
              className={clsx(
                PANEL_BORDER,
                "cx-overflow-x-auto cx-px-6 cx-py-4"
              )}
            >
              <div
                className={clsx(
                  TABLE_MIN_WIDTH,
                  "cx-flex cx-flex-col cx-gap-4"
                )}
              >
                <div className={clsx(TABLE_GRID, "cx-py-2")}>
                  {headerLabels.map((label) => (
                    <span
                      key={label}
                      className="cx-text-sm cx-font-normal cx-text-[#666] cx-min-w-0 cx-whitespace-nowrap"
                    >
                      {label}
                    </span>
                  ))}
                </div>

                <div className="cx-h-px cx-w-full cx-bg-[#DDD]" aria-hidden />

                {vendors.map((vendor, index) => {
                  const utilisation = vendor.outstandingAmount ?? null;
                  const available = resolveAvailable(vendor);
                  const limitReached =
                    blockWhenSubLimitReached &&
                    available != null &&
                    available <= 0;
                  const canRequest =
                    !drawdownDisabled &&
                    !!onRequestDrawdown &&
                    !!vendor.vendorId &&
                    !limitReached;

                  return (
                    <Fragment key={vendor.loanVendorId}>
                      {index > 0 && (
                        <div
                          className="cx-h-px cx-w-full cx-bg-[#DDD]"
                          aria-hidden
                        />
                      )}
                      <div className={clsx(TABLE_GRID, "cx-py-2")}>
                        <span className="cx-text-sm cx-font-normal cx-text-black cx-min-w-0 cx-truncate">
                          {vendor.name || DASH}
                        </span>
                        <span className="cx-min-w-0">
                          <AmountText amount={vendor.subLimitAmount} />
                        </span>
                        <span className="cx-min-w-0">
                          <AmountText amount={utilisation} />
                        </span>
                        <span className="cx-min-w-0">
                          <AmountText
                            amount={available}
                            className={
                              available != null && available < 0
                                ? "cx-text-[#D92D20]"
                                : undefined
                            }
                          />
                        </span>
                        <span className="cx-text-sm cx-font-normal cx-text-black cx-min-w-0">
                          {formatContractExpiry(
                            vendor.vendorContractExpiryDate
                          )}
                        </span>
                        <span className="cx-min-w-0">
                          {limitReached ? (
                            <span className="cx-inline-flex cx-items-center cx-gap-1 cx-text-sm cx-font-semibold cx-text-[#DC6803]">
                              <WarningIcon width={14} height={14} aria-hidden />
                              Limit reached
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={(event) =>
                                handleRequestDrawdown(event, vendor)
                              }
                              disabled={!canRequest}
                              className={clsx(
                                "cx-inline-flex cx-items-center cx-gap-1 cx-text-sm cx-font-semibold cx-whitespace-nowrap",
                                canRequest
                                  ? "cx-text-interaction-button-primary-default hover:cx-text-interaction-button-primary-hover"
                                  : "cx-text-[#A4A7AE] cx-cursor-not-allowed"
                              )}
                            >
                              <PlusIcon width={14} height={14} aria-hidden />
                              Request Drawdown
                            </button>
                          )}
                        </span>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
